import axios from "src/utils/axios";
import { freedMesaFields, mergeMesasKeepingIdentity } from "src/utils/tpvSyncCore";
import { isRecentLocalFetch, markLocalCheckoutFetch } from "src/utils/localCheckoutFetchTracker";
import { createSlice } from "@reduxjs/toolkit";
import { mesaKicked } from "src/redux/actions/mesaKick";
import { cleanSuccess, getOrdersSuccess } from "./order";
import {
  cleanHistoryMesa,
  resetCart,
  resetCheckoutToDelete,
  setDeleteMode,
  syncCartCheckoutComplete,
} from "./product";
import { getSessionSuccess } from "./session";
import {
  mesaCheckPrintedLocally,
  mesaCheckPrintedRevert,
  mesaComandadaLocally,
} from "src/redux/actions/checkPrinted";
import {
  beginMesasSnapshot,
  guardMesasSnapshot,
  noteMesaWrite,
  reconcileIncomingCheckPrinted,
} from "src/utils/mesaFreshness";

// ----------------------------------------------------------------------

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** Parchea las mesas que cumplen `match` en `mesas`, `mesa` y `mesasShown`. */
function patchMesas(state, match, patch) {
  let touched = false;
  state.mesas = state.mesas.map((m) => {
    if (!match(m)) return m;
    touched = true;
    return patch(m);
  });
  if (!touched) return;
  if (state.mesa && match(state.mesa)) state.mesa = patch(state.mesa);
  if (Array.isArray(state.mesasShown)) {
    state.mesasShown = state.mesasShown.map((m) => (match(m) ? patch(m) : m));
  }
}

const initialState = {
  isLoading: false,
  createdSuccess: false,
  updatedSuccess: false,
  error: false,
  selectedId: -1,
  // Última mesa seleccionada (persistida): al recargar se restaura la
  // selección con su cuenta, o se limpia el carrito (ver MesaLeaseBridge).
  lastSelectedId: -1,
  mesas: [],
  mesasShown: [],
  mesa: null,
  mesaHub: null,
  activeStep: -1,
  zonas: [],
  selectedZonaIdCreate: -1,
  selectedZonaId: -1,
  lastTimeRequest: null,
  selectedChangeMesaZonaId: -1,
  openComensales: false,
  changingMesaLoading: false,
  tickets: [],
  isClosingMesa: null,
  layoutDesign: [],
};

const slice = createSlice({
  name: "mesas",
  initialState,
  extraReducers: (builder) => {
    // Me han quitado la mesa abierta: el diálogo de comensales era de ella.
    builder.addCase(mesaKicked, (state) => {
      state.openComensales = false;
    });
    // Badge «cuenta impresa» optimista (ver redux/actions/checkPrinted.js).
    builder.addCase(mesaCheckPrintedLocally, (state, action) => {
      const { sessionId, mesaId, at } = action.payload || {};
      patchMesas(
        state,
        (m) =>
          m?.ocupada &&
          ((mesaId != null && sameId(m.id, mesaId)) ||
            (sessionId != null && (sameId(m.sessionId, sessionId) || sameId(m.idSession, sessionId)))),
        // Igual que el servidor: no se pisa una marca anterior (re-imprimir
        // mantiene el «esperando desde») y se quita «pago solicitado».
        (m) => ({ ...m, checkPrintedAt: m.checkPrintedAt || at, requestPayment: false }),
      );
    });
    builder.addCase(mesaCheckPrintedRevert, (state, action) => {
      const { mesaId, at } = action.payload || {};
      patchMesas(
        state,
        (m) => sameId(m?.id, mesaId) && m.checkPrintedAt === at,
        (m) => ({ ...m, checkPrintedAt: null }),
      );
    });
    builder.addCase(mesaComandadaLocally, (state, action) => {
      const { mesaId } = action.payload || {};
      patchMesas(
        state,
        (m) => sameId(m?.id, mesaId) && m.checkPrintedAt != null,
        (m) => ({ ...m, checkPrintedAt: null }),
      );
    });
  },
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    setIsClosingMesa(state, action) {
      state.isClosingMesa = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.createdSuccess = false;
    },

    // GET PRODUCTS
    getMesasSuccess(state, action) {
      state.isLoading = false;

      const ordered = [...(action.payload || [])].sort((a, b) => {
        if (a.zonaId === b.zonaId) return a.id_mesa < b.id_mesa ? -1 : 1;
        return a.zonaId < b.zonaId ? -1 : 1;
      });
      // Fase 3 · Conservar la identidad de las mesas sin cambios para que
      // las fichas memoizadas del plano no se repinten todas.
      state.mesas = mergeMesasKeepingIdentity(state.mesas, ordered);
      state.createdSuccess = false;
    },

    updateMesaFromHub(state, action) {
      state.mesaHub = action.payload;
    },

    // GET PRODUCT
    getMesaSuccess(state, action) {
      state.isLoading = false;
      state.mesa = action.payload;
      state.createdSuccess = false;
    },
    getFirstMesaSuccess(state, action) {
      state.isLoading = false;

      if (state.mesas.length > 0) state.mesa = state.mesas[0];
    },
    getMesaByID(state, action) {
      state.mesa = state.mesas.find((m) => m.id == action.payload);
    },
    getMesaSuccessFromHub(state, action) {
      var mesa = action.payload;
      state.mesas = state.mesas.map((m) => {
        if (m.id == mesa.id) {
          return {
            ...mesa,
            // OJO: NO sobrescribir `state` con el del store viejo. El
            // backend calcula `mesa.State` (PENDING/IN_PROGRESS/DONE) en
            // cada push tras un cambio del KDS — esto es lo que enciende
            // el parpadeo "cocina lista" en el tile. Si pisamos el state
            // entrante con `m.state`, el badge DONE nunca llega al UI y
            // el parpadeo se queda apagado aunque haya producto Ready.
            sessionCreated:
              m.ocupada &&
              m.sessionCreated &&
              !action.payload.sessionCreated &&
              m.idSession === action.payload.idSession
                ? m.sessionCreated
                : mesa.sessionCreated,
          };
        } else return m;
      });

      if (state.selectedId != -1) {
        state.mesa = state.mesas.find((m) => m.id == state.selectedId);
      }

      if (state.selectedZonaId != -1) {
        state.mesasShown = state.mesas.filter(
          (s) => s.zonaId == state.selectedZonaId,
        );
      }
    },
    getMesasSuccessFromHub(state, action) {
      state.mesas = mergeMesasKeepingIdentity(state.mesas, action.payload);
      if (state.selectedId != -1) {
        state.mesa = state.mesas.find((m) => m.id == state.selectedId);
      }

      if (state.selectedZonaId != -1) {
        state.mesasShown = state.mesas.filter(
          (s) => s.zonaId == state.selectedZonaId,
        );
      }
    },
    // F7 · Optimistic update para el merge in-place — actualiza el
    // estado local ANTES de la respuesta HTTP para que la UI no muestre
    // el flicker de "desconectada" mientras el backend procesa.
    optimisticAddMemberToGroup(state, action) {
      const { newMemberId, groupId, leaderId, layoutUpdates } = action.payload || {};
      const leader = state.mesas.find((m) => m.id === leaderId);
      const memberIdx = state.mesas.findIndex((m) => m.id === newMemberId);
      if (memberIdx < 0) return;
      const oldMember = state.mesas[memberIdx];
      // Absorción de cuenta (sumamos la del miembro a la líder).
      if (leader) {
        leader.cuenta = (leader.cuenta || 0) + (oldMember.cuenta || 0);
        if (!leader.ocupada && oldMember.ocupada) {
          leader.ocupada = true;
          leader.sessionId = oldMember.sessionId;
          leader.sessionCreated = oldMember.sessionCreated;
        }
      }
      // El miembro pasa a formar parte del grupo, vacío.
      state.mesas[memberIdx] = {
        ...oldMember,
        mergedGroupId: groupId,
        mergedRole: "member",
        sessionId: null,
        ocupada: false,
        cuenta: 0,
        comensales: 0,
        requestPayment: false,
        requestCamarero: false,
        checkPrintedAt: null,
      };
      // Layout updates opcionales (posiciones nuevas tras shift).
      if (Array.isArray(layoutUpdates)) {
        for (const u of layoutUpdates) {
          const idx = state.mesas.findIndex((m) => m.id === u.mesaId);
          if (idx < 0) continue;
          if (u.deviceType === "mobile") {
            state.mesas[idx] = {
              ...state.mesas[idx],
              posColMobile: u.posCol,
              posRowMobile: u.posRow,
            };
          } else {
            state.mesas[idx] = {
              ...state.mesas[idx],
              posCol: u.posCol,
              posRow: u.posRow,
            };
          }
        }
      }
    },
    // F10 · Optimistic para el editor del plano — aplica las nuevas
    // posiciones al store SIN esperar la respuesta HTTP. El TPV refresca
    // instantáneamente sin el flicker "vuelve a la posición vieja
    // y luego se mueve".
    optimisticSaveLayout(state, action) {
      const { items, deviceType } = action.payload || {};
      if (!Array.isArray(items)) return;
      const isMobile = deviceType === "mobile";
      for (const it of items) {
        const idx = state.mesas.findIndex((m) => m.id === it.mesaId);
        if (idx < 0) continue;
        if (isMobile) {
          state.mesas[idx] = {
            ...state.mesas[idx],
            posColMobile: it.posCol,
            posRowMobile: it.posRow,
          };
        } else {
          state.mesas[idx] = {
            ...state.mesas[idx],
            posCol: it.posCol,
            posRow: it.posRow,
          };
        }
      }
    },
    optimisticResetLayout(state, action) {
      const { zonaId, deviceType } = action.payload || {};
      const isMobile = deviceType === "mobile";
      for (let i = 0; i < state.mesas.length; i++) {
        if (zonaId != null && state.mesas[i].zonaId !== zonaId) continue;
        if (isMobile) {
          state.mesas[i] = { ...state.mesas[i], posColMobile: null, posRowMobile: null };
        } else {
          state.mesas[i] = { ...state.mesas[i], posCol: null, posRow: null };
        }
      }
    },
    // F73 · Aplica en memoria un patch masivo a mesas concretas —
    // idéntico al bulk que se envía al backend. Se usa desde el
    // handleLayoutSave del editor para que Redux tenga los nuevos
    // spanCols/spanRows/rotationDeg/shape/halfScale antes del refetch,
    // eliminando el "salto" visual entre exit-editor y llegada del
    // getMesasSuccess del bulkSaveMesas.
    optimisticApplyMesaPatches(state, action) {
      const items = action.payload;
      if (!Array.isArray(items)) return;
      for (const it of items) {
        const idx = state.mesas.findIndex((m) => m.id === it.id);
        if (idx < 0) continue;
        const patch = { ...state.mesas[idx] };
        if (it.spanCols != null) patch.spanCols = it.spanCols;
        if (it.spanRows != null) patch.spanRows = it.spanRows;
        if (it.rotationDeg != null) patch.rotationDeg = it.rotationDeg;
        if (it.shape != null) patch.shape = it.shape;
        if (it.halfScale != null) patch.halfScale = it.halfScale;
        if (it.nombre != null) patch.nombre = it.nombre;
        if (it.capacidad != null) patch.capacidad = it.capacidad;
        if (it.id_mesa != null) patch.id_mesa = it.id_mesa;
        if (it.posCol != null) patch.posCol = it.posCol;
        if (it.posRow != null) patch.posRow = it.posRow;
        if (it.posColMobile != null) patch.posColMobile = it.posColMobile;
        if (it.posRowMobile != null) patch.posRowMobile = it.posRowMobile;
        state.mesas[idx] = patch;
      }
    },
    // Merge inicial (sin grupo previo) también optimista: crea groupId
    // F39 · Sustituye el groupId temporal por el real que devuelve el
    // backend. Se llama tras mergeMesas — evita el flicker que produce
    // getMesas() (que reemplaza el array entero).
    replaceOptimisticGroupId(state, action) {
      const { fromGroupId, toGroupId } = action.payload || {};
      if (!fromGroupId || !toGroupId) return;
      for (const m of state.mesas) {
        if (m.mergedGroupId === fromGroupId) m.mergedGroupId = toGroupId;
      }
    },
    // temporal y marca leader/members. Sustituido por el server tras
    // getMesas si necesitase corregirse.
    optimisticCreateGroup(state, action) {
      const { mesaIds, leaderId, groupId, layoutUpdates } = action.payload || {};
      const leader = state.mesas.find((m) => m.id === leaderId);
      if (!leader) return;
      // La líder absorbe cuentas de los miembros que se van a unir.
      const memberIds = mesaIds.filter((id) => id !== leaderId);
      let sumaCuenta = leader.cuenta || 0;
      let inheritedSession = leader.sessionId;
      let inheritedSessionCreated = leader.sessionCreated;
      let becomesOcupada = leader.ocupada;
      for (const mid of memberIds) {
        const mIdx = state.mesas.findIndex((m) => m.id === mid);
        if (mIdx < 0) continue;
        const oldM = state.mesas[mIdx];
        sumaCuenta += oldM.cuenta || 0;
        if (!becomesOcupada && oldM.ocupada) {
          becomesOcupada = true;
          inheritedSession = oldM.sessionId;
          inheritedSessionCreated = oldM.sessionCreated;
        }
        state.mesas[mIdx] = {
          ...oldM,
          mergedGroupId: groupId,
          mergedRole: "member",
          sessionId: null,
          ocupada: false,
          cuenta: 0,
          comensales: 0,
          requestPayment: false,
          requestCamarero: false,
          checkPrintedAt: null,
        };
      }
      // Actualizamos la líder.
      const leaderIdx = state.mesas.findIndex((m) => m.id === leaderId);
      if (leaderIdx >= 0) {
        state.mesas[leaderIdx] = {
          ...state.mesas[leaderIdx],
          mergedGroupId: groupId,
          mergedRole: "leader",
          cuenta: sumaCuenta,
          ocupada: becomesOcupada,
          sessionId: inheritedSession,
          sessionCreated: inheritedSessionCreated,
        };
      }
      if (Array.isArray(layoutUpdates)) {
        for (const u of layoutUpdates) {
          const idx = state.mesas.findIndex((m) => m.id === u.mesaId);
          if (idx < 0) continue;
          if (u.deviceType === "mobile") {
            state.mesas[idx] = { ...state.mesas[idx], posColMobile: u.posCol, posRowMobile: u.posRow };
          } else {
            state.mesas[idx] = { ...state.mesas[idx], posCol: u.posCol, posRow: u.posRow };
          }
        }
      }
    },
    // 2026-10-27 · Al mover la cuenta a otra mesa los comensales viajan con
    // ella (el API hace lo mismo en Mesas/ChangeMesa): a una mesa libre pasan
    // tal cual y a una ocupada se suman. Se aplica antes de seleccionar la
    // mesa destino para que el TPV no vuelva a pedir comensales mientras
    // llega el getMesas().
    carryMesaComensales(state, action) {
      const { from, to } = action.payload || {};
      if (!from || !to || from === to) return;
      const origin = state.mesas.find((m) => m?.id == from);
      const dest = state.mesas.find((m) => m?.id == to);
      if (!origin || !dest) return;
      const moved = Math.max(0, Number(origin.comensales) || 0);
      const destCount = dest.ocupada ? Math.max(0, Number(dest.comensales) || 0) + moved : moved;
      state.mesas = state.mesas.map((m) => {
        if (m?.id == from) return { ...m, comensales: 0 };
        if (m?.id == to) return { ...m, comensales: destCount };
        return m;
      });
      if (state.mesa?.id == from) state.mesa = { ...state.mesa, comensales: 0 };
      else if (state.mesa?.id == to) state.mesa = { ...state.mesa, comensales: destCount };
    },
    updateMesaSession(state, action) {
      const { mesa, session, cuenta } = action.payload;

      state.mesas = state.mesas.map((m) => {
        if (m && m?.id == mesa) {
          if (state.mesa && state.mesa?.id == m.id) {
            state.mesa = {
              ...m,
              sessionId: session,
              ocupada: true,
              cuenta: cuenta || m.cuenta,
            };
          }
          return { ...m, sessionId: session, cuenta: cuenta };
        }
        return m;
      });
    },

    // Ajuste local de la cuenta de UNA mesa (cobro parcial): parchea solo
    // `cuenta` sobre la ficha ACTUAL del store. `updateMesaSuccess` con una
    // copia del componente (capturada antes del POST) reescribía la mesa
    // entera y devolvía campos caducados (badge «cuenta impresa», avisos…).
    adjustMesaCuentaLocal(state, action) {
      const { mesaId, delta } = action.payload || {};
      const d = Number(delta);
      if (mesaId == null || !Number.isFinite(d) || d === 0) return;
      patchMesas(
        state,
        (m) => sameId(m?.id, mesaId),
        (m) => ({
          ...m,
          cuenta: Math.max(0, Math.round(((Number(m.cuenta) || 0) + d) * 100) / 100),
        }),
      );
    },
    updateMesaSuccess(state, action) {
      state.isLoading = false;

      if (action.payload.cuenta < 0) action.payload.cuenta = 0;

      if (state.mesa?.id == action.payload.id) state.mesa = action.payload;

      state.mesas = state.mesas.map((m) => {
        if (m.id != action.payload.id) return m;

        if (
          m.ocupada &&
          m.sessionCreated &&
          !action.payload.sessionCreated &&
          m.idSession === action.payload.idSession
        ) {
          return {
            ...action.payload,
            sessionCreated: m.sessionCreated,
          };
        }
        return action.payload;
      });

      // order mesas by id_mesa and id_zona
      state.mesas = state.mesas.sort((a, b) => {
        if (a.zonaId === b.zonaId) return a.id_mesa < b.id_mesa ? -1 : 1;
        else return a.zonaId < b.zonaId ? -1 : 1;
      });

      state.updatedSuccess = true;
    },

    updateMesaPayment(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((m) => {
        if (m.id != action.payload.id) return m;
        return action.payload;
      });

      if (state.mesa?.id == action.payload.id) state.mesa = action.payload;
    },

    createMesaSuccess(state, action) {
      state.isLoading = false;

      let mesa = action.payload;
      let existMesa = state.mesas.find((m) => m.id == mesa.id);

      if (!existMesa) {
        state.mesas = [...state.mesas, mesa];

        // order mesas by id_mesa and id_zona
        state.mesas = state.mesas.sort((a, b) => {
          if (a.zonaId === b.zonaId) return a.id_mesa < b.id_mesa ? -1 : 1;
          else return a.zonaId < b.zonaId ? -1 : 1;
        });

        state.createdSuccess = true;
      }
    },

    // 2026-10-14 · «Reabrir ticket»: la mesa vuelve a estar ocupada con la
    // sesión reabierta y su cuenta (respuesta de Data/ReOpenMesa). El eco del
    // tiempo real de este dispositivo se descarta, así que sin esto el plano
    // seguía pintando la mesa libre hasta recargar.
    mesaReopened(state, action) {
      const { idMesa, sessionId, cuenta } = action.payload || {};
      if (idMesa == null) return;
      const patch = (m) => ({
        ...m,
        ocupada: true,
        sessionId: sessionId ?? m.sessionId,
        cuenta: cuenta != null && Number.isFinite(Number(cuenta)) ? Number(cuenta) : m.cuenta,
      });
      state.mesas = state.mesas.map((m) => (m && String(m.id) === String(idMesa) ? patch(m) : m));
      if (state.mesa && String(state.mesa.id) === String(idMesa)) state.mesa = patch(state.mesa);
    },

    setSelectedIdMesaSuccess(state, action) {
      state.isLoading = false;
      state.selectedId = action.payload;
      state.lastSelectedId = action.payload;
      state.mesa = state.mesas.find((m) => m.id == action.payload);
    },

    activateAllSuccess(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((mesa) => {
        return { ...mesa, available: true };
      });
    },

    disableAllSuccess(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((mesa) => {
        return { ...mesa, available: false };
      });
    },

    activateOneSuccess(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((mesa) => {
        if (mesa.id === action.payload) return { ...mesa, available: true };
        else return mesa;
      });

      if (state.mesa?.id == action.payload)
        state.mesa = { ...state.mesa, available: false };
    },

    disableOneSuccess(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((mesa) => {
        if (mesa.id === action.payload)
          return { ...mesa, available: false, justCartaQr: true };
        else return mesa;
      });

      if (state.mesa?.id == action.payload)
        state.mesa = { ...state.mesa, available: false };
    },

    activateJustCartaQrSuccess(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((mesa) => {
        if (mesa.id === action.payload) return { ...mesa, justCartaQr: true };
        else return mesa;
      });

      if (state.mesa?.id == action.payload)
        state.mesa = { ...state.mesa, justCartaQr: false };
    },

    disableJustCartaQrSuccess(state, action) {
      state.isLoading = false;
      state.mesas = state.mesas.map((mesa) => {
        if (mesa.id === action.payload) return { ...mesa, justCartaQr: false };
        else return mesa;
      });

      if (state.mesa?.id == action.payload)
        state.mesa = { ...state.mesa, justCartaQr: false };
    },

    onNextStep(state, action) {
      state.activeStep = action.payload;
    },
    onBackStep(state, action) {
      state.activeStep = action.payload;
    },
    onResetStep(state, action) {
      state.activeStep = -1;
    },
    // 2026-09-29 · Rollback del cierre optimista de mesa al cobrar: si el
    // POST de cobro falla, devolvemos la mesa al estado previo (el refetch
    // posterior trae la verdad del servidor).
    restoreMesaSnapshot(state, action) {
      const snap = action.payload;
      if (!snap?.id) return;
      state.isLoading = false;
      state.mesas = state.mesas.map((m) => (m.id == snap.id ? snap : m));
      if (state.mesa?.id == snap.id) state.mesa = snap;
    },
    // Cierre optimista de la mesa (cobro total / cerrar sin cobro). Deja la
    // ficha como la dejará el servidor (freedMesaFields: también comensales,
    // aviso de camarero, cuenta impresa…). Se parchea la ficha del plano y no
    // se sustituye por `state.mesa`, que puede ser una copia más vieja.
    // payload opcional: id de la mesa (por defecto, la seleccionada).
    closeMesaSuccess(state, action) {
      state.isLoading = true;
      const id = action?.payload ?? state.mesa?.id;
      if (id == null) return;
      const freed = freedMesaFields();
      if (state.mesa && state.mesa.id == id) state.mesa = { ...state.mesa, ...freed };
      state.mesas = state.mesas.map((m) => (m && m.id == id ? { ...m, ...freed } : m));
    },
    updateCuentaSuccess(state, action) {
      state.mesa = { ...state.mesa, cuenta: action.payload };

      state.mesas = state.mesas.map((m) => {
        return m.id === state.mesa.id ? state.mesa : m;
      });
    },
    getMesasByZonaID(state, action) {
      state.mesasShown = state.mesas.filter((m) => m.zonaId == action.payload);
    },
    deleteMesaSuccess(state, action) {
      state.mesas = state.mesas.filter((m) => m.id != action.payload);

      if (state?.mesa?.id == action.payload) {
        state.mesa = null;
      }
    },
    getZonasSuccess(state, action) {
      state.zonas = action.payload;

      if (state.zonas?.length > 0) {
        if (state.selectedZonaIdCreate) {
          var selectedZona = state.zonas.find(
            (u) => u.id == state.selectedZonaIdCreate,
          );

          if (!selectedZona) state.selectedZonaIdCreate = state?.zonas[0]?.id;
        } else state.selectedZonaIdCreate = state?.zonas[0]?.id;
      }

      state.lastTimeRequest = Date.now();
    },

    changeSelectedZonaIdCreate(state, action) {
      state.selectedZonaIdCreate = action.payload;
    },

    deleteZonaSuccess(state, action) {
      state.zonas = state.zonas.filter((z) => z.id != action.payload);

      state.mesas = state.mesas.map((m) => {
        if (m.zonaId == action.payload) {
          return { ...m, zonaId: null };
        } else return m;
      });
    },
    addZonaSuccess(state, action) {
      if (state.zonas.find((z) => z.id === action.payload.id) == null)
        state.zonas = [...state.zonas, action.payload];

      state.selectedZonaIdCreate = action.payload.id;
    },
    updateZonaSuccess(state, action) {
      state.zonas = state.zonas.map((z) => {
        if (z.id == action.payload.id) {
          return action.payload;
        } else {
          return z;
        }
      });
    },
    setZonaSelected(state, action) {
      state.selectedZonaId = action.payload;
    },
    setZonaMesaSelected(state, action) {
      state.selectedChangeMesaZonaId = action.payload;
    },
    cleanMesas(state, action) {
      state.isLoading = false;
      state.createdSuccess = false;
      state.updatedSuccess = false;
      state.error = false;
      state.selectedId = -1;
      state.lastSelectedId = -1;
      state.mesas = [];
      state.mesasShown = [];
      state.mesa = null;
      state.activeStep = -1;
      state.zonas = [];
      state.selectedZonaId = -1;
      state.lastTimeRequest = null;
      state.selectedChangeMesaZonaId = -1;
      state.openComensales = false;
      state.changingMesaLoading = false;
      state.tickets = [];
    },
    cleanMesa(state, action) {
      state.mesa = null;
    },
    acceptDeclineOrderMesa(state, action) {
      const { mesa, precio, pagado } = action.payload;
      state.mesas = state.mesas.map((m) => {
        if (m.id == mesa) {
          let pedidosPending = m.pedidos - 1;
          return {
            ...m,
            pedidos: pedidosPending < 0 ? 0 : pedidosPending,
            cuenta: pagado ? m.cuenta : m.cuenta + precio,
          };
        }
        return m;
      });
    },
    updateComensales(state, action) {
      state.openComensales = action.payload;
    },
    changeMesaLoading(state, action) {
      state.changingMesaLoading = action.payload;
    },
    getTicketsSuccess(state, action) {
      var tickets = action.payload;

      tickets = tickets.map((t) => {
        if (t.paymentMethod) {
          return {
            ...t,
            paymentMethodName: t.paymentMethod.name,
          };
        }

        return {
          ...t,
          paymentMethodName: t.paymentMethodReason
            ? t.paymentMethodReason
            : "-",
        };
      });

      state.tickets = tickets.map((t) => {
        var mesa = state.mesas.find((m) => m.id === t.idMesa);

        if (!mesa)
          return {
            ...t,
            mesa: "Mesa eliminada",
            zona: "",
          };

        var zona = state.zonas.find((z) => z.id === mesa?.zonaId);

        return {
          ...t,
          mesa: mesa.id_mesa,
          zona: zona.nombre,
        };
      });
    },
    updateLayout(state, action) {
      state.layoutDesign = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setIsClosingMesa,
  updateMesasSuccess,
  getMesasSuccess,
  getMesaSuccess,
  createMesaSuccess,
  getMesaByID,
  setSelectedIdMesaSuccess,
  mesaReopened,
  activateAllSuccess,
  activateOneSuccess,
  disableAllSuccess,
  disableOnSuccess,
  getMesaSuccessFromHub,
  updateMesaFromHub,
  getMesasSuccessFromHub,
  onNextStep,
  onBackStep,
  onResetStep,
  closeMesaSuccess,
  restoreMesaSnapshot,
  updateCuentaSuccess,
  getMesasByZonaID,
  deleteMesaSuccess,
  getZonasSuccess,
  deleteZonaSuccess,
  addZonaSuccess,
  updateZonaSuccess,
  setZonaSelected,
  setZonaMesaSelected,
  cleanMesa,
  cleanMesas,
  updateMesaSession,
  carryMesaComensales,
  acceptDeclineOrderMesa,
  updateComensales,
  updateMesaSuccess,
  changeMesaLoading,
  updateMesaPayment,
  stopLoading,
  changeSelectedZonaIdCreate,
  getFirstMesaSuccess,
  updateLayout,
  optimisticAddMemberToGroup,
  optimisticCreateGroup,
  optimisticSaveLayout,
  optimisticResetLayout,
  optimisticApplyMesaPatches,
  replaceOptimisticGroupId,
} = slice.actions;

// ----------------------------------------------------------------------

export function getTickets() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const userId = window.localStorage.getItem("userId");
      const response = await axios.get(`ticket/getTickets/${userId}`);
      dispatch(slice.actions.getTicketsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function nextStep(id) {
  return async (dispatch) => {
    dispatch(slice.actions.onNextStep(id));
  };
}

export function backStep(id) {
  return async (dispatch) => {
    dispatch(slice.actions.onBackStep(id));
  };
}

export function resetStep() {
  return async (dispatch) => {
    dispatch(slice.actions.onResetStep());
  };
}

// `fresh`: recarga provocada por un evento en tiempo real, un resync o una
// escritura propia. No se une a un GET idéntico ya en vuelo (dedupe de
// axios): ese GET pudo salir ANTES del cambio y devolvería el plano viejo.
export function getMesas({ fresh = false } = {}) {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());
    try {
      const user = window.localStorage.getItem("userId");

      if (user) {
        const startedAt = beginMesasSnapshot();
        const response = await axios.get(
          `mesas/restaurante/${user}`,
          fresh ? { skipDedupe: true } : undefined,
        );
        // Una mesa escrita (evento/optimista) mientras el GET iba en vuelo
        // es más nueva que la de la respuesta: se conserva la del store.
        const current = getState?.()?.mesas?.mesas;
        dispatch(
          slice.actions.getMesasSuccess(
            guardMesasSnapshot(response.data.mesas, current, startedAt),
          ),
        );
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateSessionCuenta(cuenta, idSession) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.updateCuentaSuccess(cuenta));
      await axios.post(
        `data/cuenta`,
        JSON.stringify({
          idSession: idSession,
          cuenta: cuenta,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        },
      );
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      dispatch(slice.actions.hasError(err));
    }
  };
}

// ----------------------------------------------------------------------

export function receiveMesasFromHub(mesas) {
  return async (dispatch) => {
    dispatch(slice.actions.getMesasSuccessFromHub(mesas));
  };
}

/** Cobro parcial: resta lo cobrado de la cuenta de la mesa (sin pisar el resto). */
export function adjustMesaCuenta(mesaId, delta) {
  return (dispatch) => {
    if (mesaId == null) return;
    noteMesaWrite(mesaId);
    dispatch(slice.actions.adjustMesaCuentaLocal({ mesaId, delta }));
  };
}

export function receiveMesaFromHub(mesa) {
  return async (dispatch, getState) => {
    const existing = mesa
      ? (getState?.()?.mesas?.mesas || []).find((m) => sameId(m?.id, mesa.id))
      : null;
    if (mesa?.id != null) noteMesaWrite(mesa.id);
    dispatch(slice.actions.getMesaSuccessFromHub(reconcileIncomingCheckPrinted(existing, mesa)));
  };
}

export function getMesa(idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/mesas/${idMesa}`);
      dispatch(slice.actions.getMesaSuccess(response.data.mesa));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createMesa(mesa_add) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const userId = window.localStorage.getItem("userId");
      const response = await axios.post(
        `mesas`,
        JSON.stringify({
          zona: mesa_add.zona,
          idRestaurante: userId,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        },
      );
      const { mesa } = response.data;
      dispatch(
        slice.actions.addZonaSuccess({
          nombre: mesa_add.zona,
          id: mesa.zonaId,
        }),
      );
      dispatch(slice.actions.createMesaSuccess(mesa));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateMesa(mesa) {
  return async (dispatch) => {
    //dispatch(slice.actions.startLoading());
    try {
      const userId = window.localStorage.getItem("userId");
      await axios.put(
        `mesas`,
        JSON.stringify({
          ID: mesa.id,
          Id_mesa: mesa.id_mesa,
          ZonaId: mesa.zonaId,
          IdRestaurante: userId,
          Comensales: mesa.comensales,
          Nombre: mesa.nombre,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        },
      );
      dispatch(slice.actions.updateMesaSuccess(mesa));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// Editor masivo (vista bulk): envía un array `mesas` con items
// {id, nombre, capacidad, zonaId, available, deleted, id_mesa}. El
// backend procesa cada uno (crear/borrar/actualizar) y devuelve la
// lista resultante de mesas del restaurante. Refrescamos el slice con
// `getMesasSuccess` para tener la fuente única de verdad alineada con BD.
export function bulkSaveMesas(items) {
  return async (dispatch) => {
    try {
      const res = await axios.post("/mesas/bulk", { mesas: items || [] });
      const mesas = res?.data?.mesas;
      if (Array.isArray(mesas)) {
        dispatch(slice.actions.getMesasSuccess(mesas));
      }
      return mesas || [];
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteMesa(idMesa) {
  return async (dispatch) => {
    try {
      await axios.delete(`/mesas/${idMesa}`);
      dispatch(slice.actions.deleteMesaSuccess(idMesa));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function disableAll(idRestaurante) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.disableAllSuccess());
      await axios.post(`mesas/disableAll/${idRestaurante}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function activateAll(idRestaurante) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.activateAllSuccess());
      await axios.post(`mesas/activateAll/${idRestaurante}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function activateOne(idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.activateOneSuccess(idMesa));
      await axios.post(`mesas/activate/${idMesa}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function disableOne(idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.disableOneSuccess(idMesa));
      await axios.post(`mesas/disable/${idMesa}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function activateJustCartaQr(idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.activateJustCartaQrSuccess(idMesa));
      await axios.post(`mesas/activate/payment/${idMesa}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function disableJustCartaQr(idMesa) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.disableJustCartaQrSuccess(idMesa));
      await axios.post(`mesas/disable/payment/${idMesa}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

/////////////////////////////////////////////////////////////////////
export function AddZona(zona) {
  return async (dispatch) => {
    try {
      // BUG fix 2026-07-28 · antes hacíamos `dispatch(addZonaSuccess(zona))`
      // ANTES del POST con `zona` sin `id` ni `idRestaurante`. Dos consecuencias:
      //   1) El backend recibía `IdRestaurante` undefined → 404 silencioso
      //      (el catch de abajo no muestra el error).
      //   2) El reducer `addZonaSuccess` deduplica por `z.id === payload.id`;
      //      con ids `undefined` la segunda zona matcheaba la primera y no
      //      se añadía → el user solo veía UNA zona en el wizard.
      // Ahora hacemos POST primero con `idRestaurante` del localStorage y
      // usamos la respuesta real (con `id` de BD) para el reducer.
      const idRestaurante =
        zona.idRestaurante || window.localStorage.getItem("userId");
      const res = await axios.post(
        `zonas`,
        JSON.stringify({
          nombre: zona.nombre,
          idRestaurante,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        },
      );
      const created = res?.data?.zona;
      if (created?.id) {
        dispatch(addZonaSuccess(created));
      } else {
        // Fallback defensivo: si el backend no devolvió zona con id,
        // recargamos toda la lista para no dejar el store desincronizado.
        dispatch(GetZonas());
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
      // Rethrow para que el caller (StepZonas) pueda mostrar snackbar.
      throw error;
    }
  };
}

export function GetZonas() {
  return async (dispatch) => {
    try {
      var response = await axios.get(
        `zonas/restaurante/${window.localStorage.getItem("userId")}`,
      );
      const { zonas } = response.data;
      dispatch(getZonasSuccess(zonas));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function DeleteZona(idZona) {
  return async (dispatch) => {
    try {
      dispatch(deleteZonaSuccess(idZona));
      await axios.delete(`zonas/${idZona}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function UpdateZona(zona) {
  return async (dispatch) => {
    try {
      dispatch(updateZonaSuccess(zona));
      await axios.put(
        `zonas`,
        JSON.stringify({
          id: zona.id,
          nombre: zona.nombre,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        },
      );
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function UpdateMesaAndRestartSync(mesa) {
  return async (dispatch) => {
    try {
      // Fase 3 · Si este dispositivo acaba de cargar el checkout de esta mesa
      // (click en el plano → navegar a la carta), ya está hidratada: no
      // repetimos data/session + getMesaAndOrders en cada montaje.
      if (mesa?.id && mesa?.sessionId && isRecentLocalFetch(mesa.id, 5000)) return;
      if (mesa?.id) markLocalCheckoutFetch(mesa.id);
      // Mesa ocupada: getMesaAndOrders no depende de la respuesta de
      // data/session → en paralelo (antes en serie, +1 RTT).
      const ordersPromise = mesa?.sessionId
        ? axios.get(`pedidos/getMesaAndOrders/${mesa?.idRestaurante}/${mesa?.id}`)
        : null;
      // Evita "unhandled rejection" si data/session no devuelve sesión.
      ordersPromise?.catch(() => {});
      axios
        .get(`data/session/${mesa.id}/${mesa?.idRestaurante}`)
        .then((response) => {
          if (response.data.session != null) {
            // 2026-09-08 · Poblar state.order.orders con `session.pedidos`
            // ANTES de que getMesaAndOrders responda — mismo idComensal
            // que el ConvertPedido de PedidoService (getMesaAndOrders lo
            // sobrescribe cuando llega, es idempotente). Sin este dispatch
            // temprano, el PaymentDialog abierto en la ventana entre
            // syncCartCheckoutComplete y getMesaAndOrders veía orders
            // vacío/stale y pintaba "sin comensal".
            if (Array.isArray(response.data.session.pedidos)) {
              dispatch(getOrdersSuccess(response.data.session.pedidos));
            }
            (
              ordersPromise ||
              axios.get(
                `pedidos/getMesaAndOrders/${mesa?.idRestaurante}/${mesa?.id}`,
              )
            )
              .then((res) => {
                dispatch(getMesaSuccess(res.data.mesa));
                dispatch(getOrdersSuccess(res.data.orders));
              });

            if (mesa.sessionId === response.data.session.id) {
              dispatch(syncCartCheckoutComplete(response.data.checkout));

              dispatch(
                updateMesaSession({
                  mesa: response.data.session.idMesa,
                  session: response.data.session.id,
                  cuenta: response.data.checkout.subtotal,
                }),
              );
            } else {
              dispatch(
                updateMesaSession({
                  mesa: response.data.session.idMesa,
                  session: response.data.session.id,
                  cuenta: 0,
                }),
              );
            }

            dispatch(getSessionSuccess(response.data.session));
          }
        });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function cleanMesaAndProducts(mesa) {
  return async (dispatch) => {
    dispatch(closeMesaSuccess(mesa?.id ?? undefined));
    dispatch(cleanHistoryMesa(mesa?.id));
    dispatch(resetCart());
    dispatch(resetCheckoutToDelete());
    dispatch(setDeleteMode(false));
    dispatch(cleanSuccess());
    // Reset del curso activo del ChipSelector — cada mesa arranca
    // con "Sin curso" para no arrastrar el estado de otra sesión.
    // Ver docs/cursos-marchas.md (fase 5b · UI camarero).
    try {
      const { changeSelectedTagProduct } = await import("src/redux/slices/categories");
      dispatch(changeSelectedTagProduct(null));
    } catch { /* import fail = no-op */ }
  };
}

// ─── F2 Unir mesas (2026-07-21) ──────────────────────────────────────
//
//   Agrupación puramente visual. Backend actualiza `MergedGroupId` +
//   `MergedRole` en las N mesas y emite `ChangeMesa` por cada una vía
//   HubMesas — el listener global refresca `state.mesas` sin nada más.
//   Los pedidos NO se mueven — la mesa "leader" recibe los pedidos
//   nuevos durante la unión (redirección backend en PedidoService.Add).
//
// F74 · `layoutItems` + `deviceType` opcionales — si vienen, backend
// aplica el layout EN LA MISMA transacción que el merge, evitando el
// flicker de dos requests separados (merge lento + saveLayout rápido
// causaban un "des-fusion visual" de ~500ms).
export function mergeMesas({ mesaIds, leaderMesaId, layoutItems, deviceType }) {
  return async () => {
    const res = await axios.post(`mesas/merge`, { mesaIds, leaderMesaId, layoutItems, deviceType });
    return res.data; // { groupId, mesaIds, leaderMesaId }
  };
}

export function unmergeMesas(groupId) {
  return async () => {
    const res = await axios.post(`mesas/unmerge/${groupId}`);
    return res.data; // { affected: [...] }
  };
}

// F7 · Añadir mesa a un grupo YA existente en 1 sola petición (sin
// unmerge+merge intermedio). El backend hace la fusión física y devuelve
// 200 vacío; el estado local se actualiza optimista con el reducer
// `optimisticAddMemberToGroup`.
// F74 · Idem mergeMesas — layoutItems + deviceType opcionales.
export function addMemberToGroup({ groupId, mesaId, layoutItems, deviceType }) {
  return async () => {
    await axios.post(`mesas/group/${groupId}/add`, { mesaId, layoutItems, deviceType });
  };
}

// F9 · Desasocia UNA mesa del grupo sin destruirlo. La mesa vuelve a
// ser autónoma vacía; la líder mantiene los pedidos absorbidos.
export function removeMemberFromGroup({ groupId, mesaId }) {
  return async () => {
    await axios.post(`mesas/group/${groupId}/removeMember`, { mesaId });
  };
}

// F9 · Alterna la persistencia del grupo. Persistente = sobrevive al
// cobro de la líder; no persistente = se disuelve al cerrar sesión.
export function setGroupPersistent({ groupId, persistent }) {
  return async () => {
    await axios.post(`mesas/group/${groupId}/persistent`, { persistent });
  };
}

// ─── F3 Layout del plano (2026-07-22) ────────────────────────────────
//
//   Persistencia de posiciones manuales (`posCol`/`posRow`) por zona.
//   El backend responde 200 sin body y emite `ChangeMesa` para cada
//   mesa afectada — el listener HubMesas actualiza el store.
//
export function saveMesaLayout({ zonaId, deviceType = "desktop", items }) {
  return async () => {
    await axios.post(`mesas/layout/save`, { zonaId, deviceType, items });
  };
}

// resetMesaLayout acepta objeto `{zonaId, deviceType}` (F4) o plano
// (compat legacy). El backend recibe `deviceType` en query para saber
// qué par de columnas resetea.
export function resetMesaLayout(arg) {
  return async () => {
    const zonaId = typeof arg === "object" ? arg?.zonaId : arg;
    const deviceType = typeof arg === "object" ? arg?.deviceType || "desktop" : "desktop";
    const params = [];
    if (zonaId != null) params.push(`zonaId=${zonaId}`);
    if (deviceType) params.push(`deviceType=${deviceType}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    await axios.post(`mesas/layout/reset${qs}`);
  };
}
